import React, {useRef, useState} from 'react';
import {PaymentRequest} from '@stripe/stripe-js';
import './CustomPaymentRequestButton.css';

interface CustomPaymentRequestButtonProps {
  // Optional so the button can be rendered for visual QA (?qaForceCTA=1) on builds where no real
  // payment request exists. When undefined, clicking is a no-op.
  paymentRequest?: PaymentRequest;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  theme?: 'default' | 'dark' | 'success' | 'warning' | 'danger';
  size?: 'small' | 'medium' | 'large';
  rounded?: 'default' | 'full';
  // You can add more custom props as needed
}

const CustomPaymentRequestButton: React.FC<CustomPaymentRequestButtonProps> = ({
  paymentRequest,
  className = '',
  style = {},
  children,
  onClick,
  disabled = false,
  loading = false,
  theme = 'default',
  size = 'medium',
  rounded = 'default',
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Build CSS classes based on props
  const cssClasses = [
    'custom-payment-request-button',
    className,
    loading && 'loading',
    disabled && 'disabled',
    theme !== 'default' && `theme-${theme}`,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (disabled || loading || isProcessing) {
      return;
    }

    // Call custom onClick if provided
    if (onClick) {
      onClick();
    }

    try {
      setIsProcessing(true);

      // Show the payment request UI (no-op when no real request is present, e.g. QA preview)
      await paymentRequest?.show();
    } catch (error) {
      console.error('Payment request failed:', error);
      // Handle error - you might want to show a toast or error message
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <button
      ref={buttonRef}
      className={cssClasses}
      style={{
        width: '100%',
        border: 'none',
        outline: 'none',
        cursor: disabled || loading || isProcessing ? 'not-allowed' : 'pointer',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      onClick={handleClick}
      disabled={disabled || loading || isProcessing}
      type="button"
    >
      {/* Custom background */}
      <div
        className="custom-button-background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      />

      {/* Button content */}
      <div
        className="custom-button-content"
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#333333',
          fontSize: '16px',
          fontWeight: 600,
          height: '100%',
          width: '100%',
        }}
      >
        {loading || isProcessing ? (
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div className="loading-spinner" />
            Processing...
          </div>
        ) : (
          children || 'Pay with Apple Pay'
        )}
      </div>
    </button>
  );
};

export default CustomPaymentRequestButton;
